import { pythonApi } from '../../../app/services/api/api'
import {
  Defects,
  DeviceReport,
  Location,
  TopFiveDevice,
  DashBoardRequest,
  Periods,
  DeviceData,
  DashboardOverview,
  DashboardLocation,
  CurrentDataRequest,
  CurrentLocationsDataRequest,
  LocationChart,
  DashboardResponse,
  ElectricityCostType,
  OverviewRequest,
  DeviceModelTypeResponse,
} from '../types'

const PeriodMapping: { [k: string]: number } = {
  [Periods.DAY]: 1,
  [Periods.WEEK]: 12,
  [Periods.MONTH]: 24,
  [Periods.YEAR]: 360,
}

const extendedApi = pythonApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentData: builder.query<any, CurrentDataRequest>({
      query: ({ deviceIds, fields }) => {
        const searchParams = new URLSearchParams()
        deviceIds.forEach((id) => searchParams.append('device_id', id.toString()))
        fields.forEach((field) => searchParams.append('fields', field))
        return {
          url: `/current/data/device/?${searchParams}`,
          method: 'GET',
        }
      },
    }),
    getCurrentLocationsData: builder.query<any, CurrentLocationsDataRequest>({
      query: (props) => {
        const searchParams = new URLSearchParams()

        props.locationIds.forEach((id) =>
          searchParams.append('location_id', id.toString()),
        )
        if (props.devices) {
          searchParams.set('devices', props.devices)
        }
        if (props.groups) {
          searchParams.set('groups', props.groups)
        }

        return {
          url: `/current/data/locations/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
    }),
    getDashboardOverview: builder.query<DashboardOverview, DashBoardRequest>({
      query: (props) => {
        const searchParams = new URLSearchParams()
        if (props.location) {
          searchParams.set('locations', props.location)
        }
        if (props.devices) {
          searchParams.set('devices', props.devices)
        }
        if (props.groups) {
          searchParams.set('groups', props.groups)
        }
        if (props.from) {
          searchParams.set('from_date', props.from)
        }
        if (props.to) {
          searchParams.set('to_date', props.to)
        }
        if (props.last_hour) {
          searchParams.set('last_hour', props.last_hour)
        }

        return {
          url: `/v2/dashboard-overview/?${searchParams.toString()}&paging=false`,
          method: 'GET',
        }
      },
    }),
    getDashboardLocation: builder.query<DashboardLocation, DashBoardRequest>({
      query: ({ from, to, location, period }) => ({
        url: `/v2/dashboard-location/${location}/?from_date=${from}&to_date=${to}&period=${period}&limit=35`,
        method: 'GET',
      }),
    }),

    // UNUSED
    getLocations: builder.query<Location.Response, DashBoardRequest>({
      query: ({ from, to, last_hour = false }) => ({
        url: `/locations-data/?from_date=${from}&to_date=${to}&last_hour=${last_hour}&paging=false`,
        method: 'GET',
      }),
    }),

    // UNUSED
    getLocationsData: builder.query<Location.Data, DashBoardRequest>({
      query: ({ from, to, location }) => ({
        url: `/locations-data/${location}/?from_date=${from}&to_date=${to}`,
        method: 'GET',
      }),
    }),

    getAllClimateDevices: builder.query({
      queryFn: async (params, queryApi, extraOptions, baseQuery) => {
        try {
          const { locationIds, from, to } = params
          if (!locationIds) {
            return { data: [] }
          }

          const responses = await Promise.all(
            locationIds.map((location) =>
              baseQuery(
                `/locations-data/${location.id}/dashboard/?from_date=${from}&to_date=${to}&limit=35`,
              ),
            ),
          )

          const result = {
            data: responses
              .reduce((agg: any, response: any) => agg.concat(response.data), [])
              // @ts-ignore
              // .filter((device) => device?.model?.type?.title === 'climate_control'),
              .filter((device) => device?.category === 'climate_control'),
          }

          return result
        } catch (error) {
          return { error: error.message || 'Could not fetch data' }
        }
      },
    }),

    // UNUSED
    getTopFiveDevices: builder.query<TopFiveDevice.Data[], DashBoardRequest>({
      //   query: () => "top/five",
      query: ({ from, to, location }) => ({
        url: `/locations-data/${location}/devices/?from_date=${from}&to_date=${to}`,
        method: 'GET',
      }),
    }),

    getLocationChartData: builder.query<LocationChart.Data[], LocationChart.Request>({
      query: ({ from, to, period = 'day', location }) => ({
        url: `/v2/dashboard-location/${location}/data/?from_date=${from}&to_date=${to}&period=${PeriodMapping[period]}`,
        method: 'GET',
      }),
    }),

    // getDeviceReportData: builder.query<DeviceReport.Response, DeviceReport.Request>({
    //   query: ({ from, to, period = 'day', device }) => ({
    //     url: `/data/device/${device}/?from_date=${from}&to_date=${to}&period=${PeriodMapping[period]}`,
    //     method: 'GET',
    //   }),
    // }),
    getDashboardNotifications: builder.query<Defects.Response, void>({
      query: () => 'defects/?search=&page=1&ordering=-start_time',
    }),
    getDeviceById: builder.query<DeviceModelTypeResponse, { id: string }>({
      query: ({ id }) => `devices-model-type/${id}/`,
    }),

    getSensiboDeviceData: builder.query<any, { id: string }>({
      query: ({ id }) => `sensibo/${id}/device-data/`,
    }),
    getACState: builder.query<any, { id: string }>({
      query: ({ id }) => `sensibo/${id}/ac-state/`,
    }),
    setACState: builder.mutation<any, any>({
      query: ({ id, acState }) => {
        return {
          url: `sensibo/${id}/ac-state/set/`,
          method: 'POST',
          body: acState,
        }
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch, getState }) => {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData('getACState', { id: arg.id }, (draft) => {
            Object.assign(draft, arg.acState)
          }),
        )

        try {
          await queryFulfilled
        } catch {
          // If mutation fails, undo the optimistic update
          patchResult.undo()
        }
      },
    }),
    patchACState: builder.mutation<any, any>({
      query: ({ id, acState }) => {
        return {
          url: `sensibo/${id}/ac-state/patch/`,
          method: 'PATCH',
          body: acState,
        }
      },
    }),
    getLocationsDevicesByLocationIds: builder.query<
      any,
      { locationIds: number[]; from?: string; to?: string }
    >({
      query: ({ locationIds, from, to }) => ({
        url: 'base-report/',
        method: 'POST',
        body: {
          location_ids: locationIds,
          from_date: from,
          to_date: to,
        },
      }),
    }),
    getConsumptionPriceByLocationId: builder.query<any, DashBoardRequest>({
      query: ({ from, to, location }) => ({
        url: `energy-price/calculate_total_cost/?from_date=${from}&to_date=${to}&location=${location}`,
        method: 'GET',
      }),
    }),
    getOverviewData: builder.query<DashboardResponse[], OverviewRequest>({
      query: ({ from_date, to_date, locations }) => {
        const searchParams = new URLSearchParams()
        if (from_date) {
          searchParams.set('from_date', from_date)
        }
        if (to_date) {
          searchParams.set('to_date', to_date)
        }
        if (locations) {
          searchParams.set('locations', locations)
        }

        return {
          url: `/location-information/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
    }),
    getLocationElectricityCost: builder.query<ElectricityCostType[], void>({
      query: () => ({
        url: `/location-electricity-cost/`,
        method: 'GET',
      }),
      providesTags: ['Cost'],
    }),
    getLocationData: builder.query<any, any>({
      query: ({ from_date, to_date, location, group }) => {
        const searchParams = new URLSearchParams()
        if (from_date) {
          searchParams.set('from_date', from_date)
        }
        if (to_date) {
          searchParams.set('to_date', to_date)
        }
        if (location) {
          searchParams.set('location', location)
        }
        if (group) {
          searchParams.set('group', group)
        }

        return {
          url: `/dashboard-location-overview/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
    }),
    getOverviewDeviceData: builder.query<any, any>({
      query: ({ from_date, to_date, device }) => {
        const searchParams = new URLSearchParams()
        if (from_date) {
          searchParams.set('from_date', from_date)
        }
        if (to_date) {
          searchParams.set('to_date', to_date)
        }
        if (device) {
          searchParams.set('device_id', device)
        }

        return {
          url: `/dashboard-device-overview/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
    }),
    updateLocationElectricityCost: builder.mutation<
      ElectricityCostType[],
      { id: number; body: ElectricityCostType }
    >({
      query: ({ id, body }) => ({
        url: `/location-electricity-cost/${id}/`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['Cost'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetLocationsQuery,
  useGetOverviewDataQuery,
  useGetCurrentLocationsDataQuery,
  useGetDashboardOverviewQuery,
  useGetLocationsDataQuery,
  useGetTopFiveDevicesQuery,
  // useGetDeviceReportDataQuery,
  useGetLocationChartDataQuery,
  useGetDeviceByIdQuery,
  useGetDashboardNotificationsQuery,
  useGetDashboardLocationQuery,
  useGetCurrentDataQuery,
  useGetSensiboDeviceDataQuery,
  useGetACStateQuery,
  useSetACStateMutation,
  usePatchACStateMutation,
  useGetAllClimateDevicesQuery,
  useGetConsumptionPriceByLocationIdQuery,
  useGetLocationElectricityCostQuery,
  useUpdateLocationElectricityCostMutation,
  useGetLocationDataQuery,
  useGetOverviewDeviceDataQuery,
} = extendedApi

export const setMultipleACStates = async (dispatch, { ids, acState }) => {
  const results = await Promise.all(
    ids.map(async (id) => {
      const action = extendedApi.endpoints.setACState.initiate({ id, acState })
      const patchResult = dispatch(
        extendedApi.util.updateQueryData('getACState', { id }, (draft) => {
          Object.assign(draft, acState)
        }),
      )

      try {
        await dispatch(action).unwrap()
      } catch {
        patchResult.undo()
      }
    }),
  )
  return results
}
